/**
 * 호버/포커스/클릭 시 정보를 표시하는 AppTooltip 컴포넌트
 *
 * - 한국어 긴 안내도 줄바꿈되도록 max-w-xs(약 320px) + whitespace-normal
 * - 키보드 사용자 / 터치 기기를 위해 triggerOn으로 hover/click/focus 모드 선택
 * - 콘텐츠가 보이는 동안 trigger의 aria-describedby에 연결되도록 useId 활용
 * - 툴팁 본체는 document.body로 portal + position:fixed 로 렌더 →
 *   overflow(가로 스크롤 등) 컨테이너 안에 있어도 잘리거나 스크롤을 유발하지 않음
 */

'use client';

import { useState, useRef, useEffect, useCallback, useId, cloneElement, isValidElement, type ReactNode, type ReactElement } from 'react';
import { createPortal } from 'react-dom';

export type AppTooltipTrigger = 'hover' | 'click' | 'focus';

export interface AppTooltipProps {
  children: ReactNode;
  content: ReactNode;
  /** AppTooltip 표시 위치 */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** 최대 너비 (기본: 320px) */
  maxWidth?: number;
  /** AppTooltip 비활성화 */
  disabled?: boolean;
  /** 표시 지연 시간 (ms, 기본: 100) */
  delay?: number;
  /** 트리거 방식 — 데스크톱 hover, 터치/접근성 click 또는 focus */
  triggerOn?: AppTooltipTrigger;
  /** 추가 클래스 */
  className?: string;
}

interface Coords {
  top: number;
  left: number;
}

export function AppTooltip({
  children,
  content,
  position = 'top',
  maxWidth = 320,
  disabled = false,
  delay = 100,
  triggerOn = 'hover',
  className = '',
}: AppTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const [mounted, setMounted] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();

  // portal은 클라이언트 마운트 이후에만 (SSR/hydration 안전)
  useEffect(() => {
    setMounted(true);
  }, []);

  const show = () => {
    if (disabled) {
      return;
    }
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const hide = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  const toggle = () => {
    if (disabled) {
      return;
    }
    setIsVisible((prev) => !prev);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // 클릭 모드: 외부 클릭 시 닫기
  useEffect(() => {
    if (triggerOn !== 'click' || !isVisible) {
      return;
    }
    const handleOutside = (event: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
        setIsVisible(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [triggerOn, isVisible]);

  // trigger 위치 기준으로 fixed 좌표 계산 (viewport 기준)
  const computeCoords = useCallback(() => {
    const el = triggerRef.current;
    if (!el) {
      return;
    }
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    switch (position) {
      case 'bottom':
        setCoords({ top: r.bottom + 8, left: cx });
        break;
      case 'top':
        setCoords({ top: r.top - 8, left: cx });
        break;
      case 'left':
        setCoords({ top: cy, left: r.left - 8 });
        break;
      case 'right':
        setCoords({ top: cy, left: r.right + 8 });
        break;
    }
  }, [position]);

  // 표시되는 동안 스크롤/리사이즈에 따라 좌표 갱신 (capture:true 로 내부 스크롤 컨테이너까지 추적)
  useEffect(() => {
    if (!isVisible) {
      return;
    }
    computeCoords();
    const onReflow = () => computeCoords();
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [isVisible, computeCoords]);

  // 위치별 정렬 transform (fixed 좌표를 앵커로 중앙/모서리 정렬)
  const transformByPosition: Record<NonNullable<AppTooltipProps['position']>, string> = {
    top: 'translate(-50%, -100%)',
    bottom: 'translateX(-50%)',
    left: 'translate(-100%, -50%)',
    right: 'translateY(-50%)',
  };

  // Arrow 클래스 (툴팁 본체 박스 기준 위치)
  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-neutral-800',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-neutral-800',
    left: 'left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-neutral-800',
    right: 'right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-neutral-800',
  };

  if (!content) {
    return <>{children}</>;
  }

  // children에 aria-describedby 연결 (단일 element일 때만)
  const enhancedChildren = (() => {
    if (!isValidElement(children)) {
      return children;
    }
    const child = children as ReactElement<Record<string, unknown>>;
    if (!isVisible) {
      return child;
    }
    return cloneElement(child, {
      'aria-describedby': tooltipId,
    });
  })();

  // 트리거 방식별 핸들러
  const triggerHandlers: Record<string, unknown> = {};
  if (triggerOn === 'hover') {
    triggerHandlers.onMouseEnter = show;
    triggerHandlers.onMouseLeave = hide;
    // 키보드 접근성 — 포커스해도 보이도록
    triggerHandlers.onFocus = show;
    triggerHandlers.onBlur = hide;
  } else if (triggerOn === 'click') {
    triggerHandlers.onClick = toggle;
  } else if (triggerOn === 'focus') {
    triggerHandlers.onFocus = show;
    triggerHandlers.onBlur = hide;
  }

  return (
    <div
      ref={triggerRef}
      className={`relative inline-flex ${className}`}
      {...triggerHandlers}
    >
      {enhancedChildren}

      {mounted && isVisible && coords &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            className="fixed z-[9999] pointer-events-none"
            style={{ top: coords.top, left: coords.left, transform: transformByPosition[position], maxWidth }}
          >
            <div className="relative">
              <div className="bg-neutral-800 text-white text-sm leading-snug rounded-lg px-3 py-2 shadow-lg whitespace-normal break-words">
                {content}
              </div>
              {/* Arrow */}
              <div className={`absolute w-0 h-0 border-4 ${arrowClasses[position]}`} />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
